/* eslint-disable no-underscore-dangle */

import { arrify } from './arrayUtils';
import { isString } from './type';

const isElement = (val) => val instanceof Element;

const constants = Object.freeze({
  attributes: {
    viewportDirection: {
      name: 'data-in-viewport-direction',
    },
    viewportPosition: {
      name: 'data-in-viewport-position',
      values: {
        above: 'above',
        below: 'below',
        hidden: 'hidden',
        intersecting: 'intersecting',
      },
    },
  },
});

/**
 * Set the direction of an observed element
 * @param {Element} target The observed element
 * @param {boolean} isIntersecting True if it is intersection the root
 */
function setDirection(target, isIntersecting) {
  const dataDirection = constants.attributes.viewportDirection.name;
  const dataPosition = constants.attributes.viewportPosition.name;
  const positionValues = constants.attributes.viewportPosition.values;

  const position = target.getAttribute(dataPosition);
  if (!isIntersecting) {
    if (position && position === positionValues.intersecting) {
      // detect out direction on first intersection
      target.setAttribute(dataDirection, `from-${position}`);
    } else {
      target.removeAttribute(dataDirection);
    }
  } else if (position && position !== positionValues.intersecting) {
    target.setAttribute(dataDirection, `from-${position}`);
  } else {
    const direction = target.getAttribute(dataDirection);
    // default onload value,
    // also takes into account multiple thresholds
    const fromPos = direction || `from-${positionValues.below}`;
    target.setAttribute(dataDirection, fromPos);
  }
}

/**
 * Set the position from the viewport of an observed element
 * @param {Element} target The observed element
 * @param {boolean} isIntersecting True if it is intersection the root
 */
function setPosition(target, {
  isAbove,
  isBelow,
  isIntersecting,
}) {
  let position;
  if (isIntersecting) {
    position = constants.attributes.viewportPosition.values.intersecting;
  } else if (isAbove) {
    position = constants.attributes.viewportPosition.values.above;
  } else if (isBelow) {
    position = constants.attributes.viewportPosition.values.below;
  } else {
    position = constants.attributes.viewportPosition.values.hidden;
  }

  target.setAttribute(constants.attributes.viewportPosition.name, position);
}

/**
 * Set the position from the viewport of an observed element from an entry
 * @param {IntersectionObserverEntry} entry The entry of an intersection
 * observer
 */
function setPositionForObserverEntry(entry) {
  const top = entry.boundingClientRect.top - entry.rootBounds.top;
  setPosition(entry.target, {
    isAbove: !entry.isIntersecting && top < 0,
    isBelow: !entry.isIntersecting && top > 0,
    isIntersecting: entry.isIntersecting,
  });
}

/**
 *
 * @param {Element[]|Element|string} targetElementsOrSelector An array of
 * elements or a css selector for querying the DOM
 * @param {function(IntersectionObserverEntry, IntersectionObserver)} entryHandler
 * A function to handle a single entry of an intersection observer
 * @param {Object} [options]
 * @param {Object} [options.apiOptions] Native Intesection Observer API options
 * @param {Element} [options.apiOptions.root]
 * @param {*} [options.apiOptions.rootMargin]
 * @param {number|number[]} [options.apiOptions.threshold]
 * @param {boolean} [options.once=false] Unobserve each target after the first
 * intersection
 * @returns {IntersectionObserver} The new intersection observer
 * @throws {TypeError}
 */
function createIntersectionObserver(targetElementsOrSelector, entryHandler, {
  apiOptions = {},
  once = false,
} = {}) {
  let targets;
  if (isString(targetElementsOrSelector)) {
    // If it is a selector
    targets = [...document.querySelectorAll(targetElementsOrSelector)];
  } else if (isElement(targetElementsOrSelector)) {
    // If it is an element
    targets = arrify(targetElementsOrSelector);
  } else if (Array.isArray(targetElementsOrSelector) && targetElementsOrSelector.every(isElement)) {
    // If it is an array of elements
    targets = targetElementsOrSelector;
  } else {
    throw new TypeError(`Unknown target: ${targetElementsOrSelector}`);
  }

  const observer = new IntersectionObserver(
    (entries, currentObserver) => {
      entries.forEach((entry) => {
        entryHandler(entry, currentObserver);
        if (once === true && entry.isIntersecting) {
          currentObserver.unobserve(entry.target);
        }
      });
    },
    apiOptions,
  );

  targets.forEach((element) => {
    observer.observe(element);
  });

  return observer;
}

/**
 * Creates a customizable single-entry intersectionObserver handler.
 *
 * Sets following `data-*` attributes on each target:
 * `data-in-viewport-direction` from-above/from-below
 * `data-in-viewport-position` above/below/intersecting
 * if a threshold % of the target is in viewport
 *
 * Based in:
 * intersection-observer-js
 * https://github.com/hanzo-es/intersection-observer-js
 *
 * @param {Object} params
 * @param {function(IntersectionObserverEntry, ...any)} params.startCallback
 * @param {function(IntersectionObserverEntry, ...any)} params.inCallback
 * @param {function(IntersectionObserverEntry, ...any)} params.outCallback
 * @param {function(IntersectionObserverEntry, ...any)} params.endCallback
 * @returns {Function}
 */
const createIntersectionObserverHandler = (params) => {
  const {
    startCallback,
    inCallback,
    outCallback,
    endCallback,
  } = params;

  // checks for valid callback function to apply
  const noop = () => {};
  const applyFunction = (cb) => ((cb && (cb instanceof Function)) ? cb : noop);

  const startHook = (entry, ...extraArgs) => {
    applyFunction(startCallback)(entry, ...extraArgs);
  };

  const inHook = (entry, ...extraArgs) => {
    applyFunction(inCallback)(entry, ...extraArgs);
  };

  const outHook = (entry, ...extraArgs) => {
    applyFunction(outCallback)(entry, ...extraArgs);
  };

  const updateIntersection = (entry, ...extraArgs) => {
    if (entry.isIntersecting) {
      inHook(entry, ...extraArgs);
    } else {
      outHook(entry, ...extraArgs);
    }
  };

  const endHook = (entry, ...extraArgs) => {
    applyFunction(endCallback)(entry, ...extraArgs);
  };

  /**
   * Handle a single entry of an Intersection Observer
   * @param {IntersectionObserverEntry} entry single intersection observer entry
   * @param  {...any} extraArgs extra arguments
   * @returns {boolean}
   */
  function interectionObserverHandler(entry, ...extraArgs) {
    // optional start callback
    startHook(entry, ...extraArgs);

    // set direction
    setDirection(entry.target, entry.isIntersecting);

    // set position
    setPositionForObserverEntry(entry);

    // Check for detection type
    updateIntersection(entry, ...extraArgs);

    // optional end callback
    endHook(entry, ...extraArgs);

    return true;
  }

  return interectionObserverHandler;
};

export {
  constants,
  createIntersectionObserver,
  createIntersectionObserverHandler,
};
